import React, { useContext, useEffect, useState } from "react";
import { apiGet, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import OButton from "components/reusable/OButton";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import dayjs from "dayjs";
import Pagination from "../Pagination";
import SubscriptionList from "./SubscriptionList";
import SubscriptionView from "./SubscriptionView";
import SubscriptionEdit from "./SubscriptionEdit";
import SubscriptionAdd from "./SubscriptionAdd";
import AuthContext from "context/AuthContext";
import { useTranslation } from "react-i18next";
import helpers from "../../utils/helpers";
import OSearch from "components/reusable/OSearch";
import CustomSelect from "components/reusable/CustomSelect";
import { statusOptions } from "utils/constants";

const SubscriptionManager = () => {
  const { t } = useTranslation();
  const { user, updatePageName } = useContext(AuthContext);
  const manager =
    user?.permission?.find((e) => e.manager === "subscription_manager") ?? {};
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isDelete, setIsDelete] = useState(false);
  const [subscriptionAdd, setSubscriptionAdd] = useState(false);
  const [subscriptionEdit, setSubscriptionEdit] = useState(false);
  const [currentItem, setCurrentItem] = useState("");
  const [subscriptionView, setSubscriptionView] = useState(false);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [filterData, setFilterData] = useState({
    category: "",
    searchKey: "",
    startDate: "",
    endDate: "",
    isReset: false,
    isFilter: false,
  });
  const [sort, setSort] = useState({
    sortBy: "createdAt",
    sortType: "desc",
  });

  const handleEdit = (item) => {
    setCurrentItem(item);
    setSubscriptionEdit(!subscriptionEdit);
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  const handleView = (item) => {
    setCurrentItem(item);
    setSubscriptionView(!subscriptionView);
  };

  const handelDelete = async (item) => {
    try {
      const path = apiPath.subscriptionDelete + "/" + item?._id;
      const result = await apiDelete(path);
      if (result?.status === 200) {
        allSubscription();
      }
    } catch (error) {
      console.log("error in get all subscription list==>>>>", error.message);
    }
  };

  const handleCategory = () => {
    setSubscriptionAdd(!subscriptionAdd);
  };

  const updatePagination = (newPage) => {
    setPage(newPage);
    setPaginationObj({ ...paginationObj, page: newPage });
  };

  const updateSubscriptionList = (data) => {
    setSubscriptionList(data?.results?.docs);
    setPaginationObj({
      ...paginationObj,
      page: helpers?.ternaryCondition(data?.success, data?.results?.page, null),
      pageCount: helpers?.ternaryCondition(
        data?.success,
        data?.results?.totalPages,
        null
      ),
      perPageItem: helpers?.ternaryCondition(
        data?.success,
        data?.results?.docs.length,
        null
      ),
      totalItems: helpers?.ternaryCondition(
        data?.success,
        data?.results?.totalDocs,
        null
      ),
    });
  };

  const shouldUpdatePagination = (data, isFilter, category) => {
    const isDeletePageCondition =
      data?.deletePage && subscriptionList?.length >= 1;
    const isFilterCondition =
      isFilter &&
      category &&
      data?.statusChange &&
      subscriptionList?.length >= 1;

    return isDeletePageCondition || isFilterCondition;
  };

  const allSubscription = async (data) => {
    try {
      const { category, startDate, endDate, searchKey, isFilter } = filterData;

      if (shouldUpdatePagination(data, isFilter, category)) {
        updatePagination(page - 1);
      }

      const payload = {
        page,
        pageSize,
        status: category,
        startDate: helpers?.ternaryCondition(
          startDate,
          dayjs(startDate).format("YYYY-MM-DD"),
          null
        ),
        endDate: helpers?.ternaryCondition(
          endDate,
          dayjs(endDate).format("YYYY-MM-DD"),
          null
        ),
        keyword: searchKey,
        sortBy: sort.sortBy,
        sortType: sort.sortType,
      };

      const path = apiPath.getSubscriptionList;
      const result = await apiGet(path, payload);
      updateSubscriptionList(result?.data);
    } catch (error) {
      console.log("error in get all subscription list==>>>>", error.message);
    }
  };

  useEffect(() => {
    allSubscription();
  }, [page, filterData, sort]);

  const handleReset = () => {
    setFilterData({
      category: "",
      searchKey: "",
      startDate: "",
      endDate: "",
      isReset: true,
      isFilter: false,
    });
    setPage(1);
    setIsDelete(true);
    setSearchTerm("");
  };

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
    setIsDelete(true);
  };
  const statusPage = (e) => {
    setPage(1);
    setFilterData({ ...filterData, category: e.target.value, isFilter: true });
    setIsDelete(true);
  };

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage,
    });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    updatePageName(t("SUBSCRIPTION_MANAGER"));
  }, []);

  const renderSubscriptionView = () => (
    <SubscriptionView
      subscriptionView={subscriptionView}
      currentItem={currentItem}
      handleView={handleView}
      allSubscription={allSubscription}
    />
  );

  const renderSubscriptionEdit = () => (
    <SubscriptionEdit
      allSubscription={allSubscription}
      handleEdit={handleEdit}
      currentItem={currentItem}
      SubscriptionList={SubscriptionList}
    />
  );

  const renderSubscriptionAdd = () => (
    <SubscriptionAdd
      subscriptionAdd={subscriptionAdd}
      allSubscription={allSubscription}
      handleCategory={handleCategory}
      subscriptionList={subscriptionList}
    />
  );

  return (
    <>
      {" "}
      <div className="bg-[#F9F9F9]">
        <div className="px-3 py-4">
          <div className="bg-white border rounded-lg border-[#E9EDF9] ">
            <form className="border-b 2xl:flex gap-2 px-4 border-b-[#E3E3E3]  py-3">
              <div className="col-span-2 items-center flex flex-wrap mb-2 2xl:mb-0">
                <div className="flex items-center lg:pt-0 pt-3 flex-wrap ">
                  <div className="flex relative items-center mb-3">
                    <OSearch
                      searchTerm={searchTerm}
                      placeholder={t("SEARCH_BY_NAME_ID")}
                      setSearchTerm={setSearchTerm}
                    />
                  </div>
                  <ODateRangePicker
                    isReset={filterData?.isReset}
                    handleDateChange={handleDateChange}
                    setIsReset={setFilterData}
                  />
                  <CustomSelect
                    id="category"
                    name="category"
                    onChange={statusPage}
                    options={statusOptions}
                    value={filterData.category}
                    placeholder="SUBCATEGORY_ALL"
                    t={t}
                  />

                  <OButton
                    label="Reset"
                    extraClasses="ml-3 mb-3"
                    style={{ borderRadius: "0.5rem" }}
                    type="button"
                    onClick={handleReset}
                  />
                </div>
              </div>
              <div className="justify-end px-4 flex items-center ms-auto mb-3">
                {(manager?.add || user?.role === "admin") && (
                  <button
                    title="Add subscription"
                    className="ml-3 py-2 rounded-lg bg-LightBlue flex text-sm px-8 items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap"
                    onClick={() => handleCategory()}
                    type="button"
                  >
                    + {t("ADD_SUBSCRIPTION")}
                  </button>
                )}
              </div>
            </form>
            <SubscriptionList
              subscriptionList={subscriptionList}
              currentItem={currentItem}
              subscriptionView={subscriptionView}
              setSort={setSort}
              sort={sort}
              page={page}
              allSubscription={allSubscription}
              handleEdit={handleEdit}
              handleView={handleView}
              handelDelete={handelDelete}
              manager={manager}
            />
            {subscriptionView && renderSubscriptionView()}
            {subscriptionEdit && renderSubscriptionEdit()}
            {subscriptionAdd && renderSubscriptionAdd()}
            {helpers.ternaryCondition(
              paginationObj?.totalItems,
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                isDelete={isDelete}
                page={page}
              />,
              null
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionManager;
